import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Chapter } from '../types/content';
import { loadChapter } from '../lib/loaders';
import ChapterQuiz from './ChapterQuiz';
import { useAuth } from '../AuthContext';
import { useStreak } from '../contexts/StreakContext';
import { autoCompleteTaskFromContent } from '../services/missionTaskCompletion';
import { ChevronLeft, BookOpen } from 'lucide-react';

/**
 * QuizRecapModule - Standalone quiz module that extracts all quizzes from a chapter
 * Used for "Rychlé opakování" (Quick Recap) missions without showing the reading content
 */
const QuizRecapModule: React.FC = () => {
  console.log('[QuizRecapModule] Component initialized');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const { trackActivity } = useStreak();

  // Get chapter ID from URL params
  const chapterId = searchParams.get('chapter');
  console.log('[QuizRecapModule] Chapter ID from URL:', chapterId);

  const [chapter, setChapter] = useState<Chapter | null>(null);
  const [allQuestions, setAllQuestions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quizCompleted, setQuizCompleted] = useState(false);

  useEffect(() => {
    console.log('[QuizRecapModule] useEffect triggered, chapterId:', chapterId);
    const loadChapterData = async () => {
      if (!chapterId) {
        console.log('[QuizRecapModule] No chapter ID provided');
        setError('No chapter specified');
        setIsLoading(false);
        return;
      }

      try {
        console.log('[QuizRecapModule] Loading chapter:', chapterId);
        setIsLoading(true);
        const loadedChapter = await loadChapter(chapterId);
        console.log('[QuizRecapModule] Chapter loaded successfully:', loadedChapter.title);
        setChapter(loadedChapter);

        // Extract all quiz questions from the chapter
        const questions = extractAllQuestions(loadedChapter);
        setAllQuestions(questions);

        if (questions.length === 0) {
          setError('Tato kapitola nemá žádné kvízy.');
        }
      } catch (err) {
        console.error('Failed to load chapter:', err);
        setError('Nepodařilo se načíst kapitolu.');
      } finally {
        setIsLoading(false);
      }
    };

    loadChapterData();
  }, [chapterId]);

  /**
   * Extract all quiz questions from a chapter (handles both single-page and multi-page)
   */
  const extractAllQuestions = (chapter: Chapter): any[] => {
    const questions: any[] = [];

    if (chapter.isMultiPage && chapter.pages && chapter.pages.length > 0) {
      // Multi-page chapter: collect questions from all pages
      chapter.pages.forEach((page, pageIndex) => {
        if (page.questions && page.questions.length > 0) {
          // Add page context to each question
          const pageQuestions = page.questions.map((q, qIndex) => ({
            ...q,
            id: `${chapter.id}_page${pageIndex}_q${qIndex}`,
            pageTitle: page.title,
            pageIndex: pageIndex
          }));
          questions.push(...pageQuestions);
        }
      });
    } else if (chapter.quiz && chapter.quiz.length > 0) {
      // Single-page chapter: use chapter-level quiz
      questions.push(...chapter.quiz.map((q, index) => ({
        ...q,
        id: `${chapter.id}_q${index}`
      })));
    }

    return questions;
  };

  const handleQuizComplete = (score: number, totalQuestions: number) => {
    setQuizCompleted(true);

    // Track streak activity - quiz questions answered
    trackActivity('quiz_answered', totalQuestions);

    // Auto-complete the mission task
    if (currentUser && chapterId) {
      autoCompleteTaskFromContent(currentUser.uid, 'quiz', chapterId)
        .then(() => console.log('Quiz task auto-completed'))
        .catch(err => console.error('Failed to auto-complete quiz task:', err));
    }
  };

  const handleBackToDashboard = () => {
    // Dispatch event to force reload of task progress in StudyPlanWidget
    window.dispatchEvent(new Event('taskCompleted'));
    navigate('/');
  };

  const handleGoToChapter = () => {
    if (chapterId) {
      navigate(`/textbook?chapter=${chapterId}`);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-orange mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Načítám kvíz...</p>
        </div>
      </div>
    );
  }

  if (error || !chapter) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Chyba
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            {error || 'Kapitola nebyla nalezena.'}
          </p>
          <button
            onClick={handleBackToDashboard}
            className="bg-brand-orange text-white px-4 py-2 rounded-lg hover:bg-brand-orange/90 transition-colors font-poppins font-medium"
          >
            Zpět na dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBackToDashboard}
              className="flex items-center gap-1 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Zpět</span>
            </button>
          </div>

          <div className="flex-1 text-center px-4">
            <h1 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">
              Rychlé opakování: {chapter.title}
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {allQuestions.length} otázek
            </p>
          </div>

          <button
            onClick={handleGoToChapter}
            className="flex items-center gap-1 text-brand-orange hover:text-brand-orange/80 transition-colors text-sm"
          >
            <BookOpen className="w-4 h-4" />
            <span className="hidden sm:inline">Číst kapitolu</span>
          </button>
        </div>
      </header>

      {/* Quiz Content */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 min-h-full flex flex-col">
        {quizCompleted ? (
          <div className="max-w-2xl mx-auto">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-500 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-green-800 dark:text-green-200 mb-2">
                Kvíz dokončen!
              </h2>
              <p className="text-green-600 dark:text-green-300 mb-4">
                Procvičil jsi všechny otázky z této kapitoly.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={handleBackToDashboard}
                  className="bg-brand-orange text-white px-6 py-3 rounded-xl border-2 border-b-[6px] border-orange-600
                           hover:bg-orange-600
                           active:border-b-2 active:translate-y-[4px]
                           transition-all duration-200 font-poppins font-bold"
                >
                  Zpět na plán
                </button>
                <button
                  onClick={() => setQuizCompleted(false)}
                  className="bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-6 py-3 rounded-xl 
                           border-2 border-b-[6px] border-gray-300 dark:border-gray-600
                           hover:border-gray-400 dark:hover:border-gray-500
                           active:border-b-2 active:translate-y-[4px]
                           transition-all duration-200 font-bold"
                >
                  Zkusit znovu
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto h-full flex flex-col">
            <ChapterQuiz
              questions={allQuestions}
              onQuizComplete={handleQuizComplete}
              onBackToContent={handleGoToChapter}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizRecapModule;
